import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Packages
import { Table } from 'react-bootstrap';
import ClipLoader from 'react-spinners/ClipLoader';
import { useTranslation } from 'react-i18next';
import { Check, Copy, Eraser, ExternalLink, Search } from 'lucide-react';

// Components
import SelectButton from '../utils/SelectButton';
import Alert from '../utils/Alert';
import TablePagination from '../utils/TablePagination';
import SurveyDailyChart from './SurveyDailyChart';

// Actions
import { get_organizations, get_areas } from '../../actions/organizationActions';
import { get_survey_schemas, list_survey_responses, lookup_survey_response, get_survey_daily_counts } from '../../actions/surveyActions';

// Utils
import { epochToLocalDate } from '../../utils/dateConverter';

import './surveyList.css';

// Small icon-only action button that copies a string to the clipboard with
// the same async-API-then-textarea-fallback pattern the payload/survey
// share buttons use elsewhere. Flashes a check for 1.5s on success.
const CopyOidButton = ({ value, label }) => {
	const [copied, setCopied] = useState(false);
	const onClick = async () => {
		let ok = false;
		if (navigator.clipboard && window.isSecureContext) {
			try { await navigator.clipboard.writeText(value); ok = true; }
			catch (e) { ok = false; }
		}
		if (!ok) {
			const ta = document.createElement('textarea');
			ta.value = value;
			ta.setAttribute('readonly', '');
			ta.style.position = 'fixed';
			ta.style.opacity = '0';
			ta.style.left = '-9999px';
			document.body.appendChild(ta);
			ta.focus();
			ta.select();
			try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
			document.body.removeChild(ta);
		}
		if (ok) {
			setCopied(true);
			setTimeout(() => setCopied(false), 1500);
		}
	};
	return (
		<button
			type='button'
			className={`sl-action-btn${copied ? ' is-copied' : ''}`}
			onClick={onClick}
			title={label}
			aria-label={label}
		>
			{copied ? <Check size={14} /> : <Copy size={14} />}
		</button>
	);
};

const SurveyList = () => {
	const { t } = useTranslation();

	const dispatch = useDispatch();

	const isFirstRender = useRef(true);

	const { user } = useSelector(state => state.auth);

	const { organizations, areas, app_config } = useSelector(state => state.organization);

	const { survey_list, survey_list_loading, survey_schemas, survey_lookup_loading, survey_daily, survey_daily_loading } = useSelector(state => state.survey);

	// The tabla renders only Tienda/Ruta/CEDIS/Fotos/Fecha/Acciones, so we
	// keep the response projection tight — the server auto-adds `fields.code`
	// and attaches `area` when `include_area` is set, so we don't list them.
	const LIST_SELECT = { _id: 1, n_images: 1, date: 1 };

	const [alert, setAlert] = useState(false);
	const [orgFilters, setOrgFilters] = useState({ organization: '', schema: '' });
	const [areaFilters, setAreaFilters] = useState({ region: '', zone: '', depot: '', group: '', route: '' });
	const [displaySurveys, setDisplaySurveys] = useState(false);

	// Two mutually-exclusive search modes for the location scope, plus a
	// no-selection default (''). Whenever the user changes mode we wipe the
	// state of every mode we're leaving behind so no invisible input can
	// sneak into the next request.
	const [searchType, setSearchType] = useState('');

	const onSearchTypeChange = (next) => {
		setSearchType(next);
		if (next !== 'route') {
			setAreaFilters({ region: '', zone: '', depot: '', group: '', route: '' });
		}
		if (next !== 'code') {
			setStoreCode('');
		}
	};

	// Some schemas carry a workspace (an org unit that owns the areas); others
	// come with `workspace: null`. When the selected schema exposes one, we
	// use it to power the area filters (region/zone/depot/group); otherwise
	// the whole area block is hidden.
	const schemaWorkspace = useMemo(() => {
		const list = Array.isArray(survey_schemas?.schemas) ? survey_schemas.schemas : [];
		const picked = list.find((s) => s?._id?.$oid === orgFilters.schema);
		return picked?.workspace?.$oid ?? '';
	}, [survey_schemas, orgFilters.schema]);

	// The org's app-config document optionally maps each schema name to a
	// friendlier alias (`data.surveysSchemas.alias: { "<schema name>": "<alias>" }`).
	// When we have a mapping, swap the schema's display name so the dropdown
	// shows the alias instead of the raw name. Falls back to the original name
	// when no alias is defined for the schema.
	const schemaOptions = useMemo(() => {
		const list = Array.isArray(survey_schemas?.schemas) ? survey_schemas.schemas : [];
		if (!orgFilters.organization || !Array.isArray(app_config)) return list;
		const orgCfg = app_config.find((c) => c?.organization?.$oid === orgFilters.organization);
		const aliasMap = orgCfg?.data?.surveysSchemas?.alias;
		if (!aliasMap || typeof aliasMap !== 'object') return list;
		return list.map((s) => {
			const alias = aliasMap[s?.name];
			return alias ? { ...s, name: alias } : s;
		});
	}, [survey_schemas, app_config, orgFilters.organization]);

	const [responseId, setResponseId] = useState('');
	const [idAlert, setIdAlert] = useState(false);

	const [dateFrom, setDateFrom] = useState('');
	const [dateTo, setDateTo] = useState('');

	const [storeCode, setStoreCode] = useState('');

	const [skip, setSkip] = useState(0);
	const [limit, setLimit] = useState(10);

	const dayStartEpoch = (yyyyMmDd) => {
		if (!yyyyMmDd) return undefined;
		const [y, m, d] = yyyyMmDd.split('-').map(Number);
		if (!y || !m || !d) return undefined;
		return Math.floor(new Date(y, m - 1, d, 0, 0, 0, 0).getTime() / 1000);
	};

	const dayEndEpoch = (yyyyMmDd) => {
		if (!yyyyMmDd) return undefined;
		const [y, m, d] = yyyyMmDd.split('-').map(Number);
		if (!y || !m || !d) return undefined;
		return Math.floor(new Date(y, m - 1, d, 23, 59, 59, 999).getTime() / 1000);
	};

	const buildDateFilters = () => ({
		from: dayStartEpoch(dateFrom),
		to: dayEndEpoch(dateTo)
	});

	// The daily-counts endpoint only defines "both dates missing → last 7
	// days"; sending just one side is undefined territory and has produced
	// degenerate requests. So for the chart we always emit a coherent pair:
	// both empty stays empty (server default), and a single filled side is
	// mirrored to produce a full one-day range (00:00 → 23:59 of that day).
	const buildChartDateFilters = () => {
		const anchor = dateFrom || dateTo;
		if (!anchor) return {};
		return {
			from: dayStartEpoch(dateFrom || dateTo),
			to: dayEndEpoch(dateTo || dateFrom),
		};
	};

	// The backend expects geographic scope filters at the root of the body as
	// arrays of ObjectIds. `searchType` decides which set we emit — the two
	// modes are mutually exclusive to avoid ambiguous intersections.
	const buildGeoFilters = () => {
		const out = {};
		if (searchType === 'route') {
			if (areaFilters.region) out.regions = [areaFilters.region];
			if (areaFilters.zone)   out.zones   = [areaFilters.zone];
			if (areaFilters.depot)  out.depots  = [areaFilters.depot];
			if (areaFilters.group)  out.groups  = [areaFilters.group];
			if (areaFilters.route)  out.routes  = [areaFilters.route];
		}
		else if (searchType === 'code') {
			const code = storeCode.trim();
			if (code !== '') out.codes = [code];
		}
		return out;
	};

	const todayIso = (() => {
		const d = new Date();
		const yyyy = d.getFullYear();
		const mm = String(d.getMonth() + 1).padStart(2, '0');
		const dd = String(d.getDate()).padStart(2, '0');
		return `${yyyy}-${mm}-${dd}`;
	})();

	useEffect(() => {
		dispatch(get_organizations());
	}, []);

	useEffect(() => {
		let lsOrganization = localStorage.getItem('organization');

		if (organizations.length <= 1) {
			setOrgFilters({
				...orgFilters,
				organization: user.organization
			});
			localStorage.setItem('organization', user.organization);
		}
		else {
			setOrgFilters({
				...orgFilters,
				organization: lsOrganization === null ? '' : lsOrganization
			});
		}
	}, [organizations]);

	useEffect(() => {
		if (orgFilters.organization !== '') {
			dispatch(get_survey_schemas({ organization: orgFilters.organization }));
		}
		else {
			setDisplaySurveys(false);
		}
	}, [orgFilters.organization]);

	// Load areas for the workspace derived from the currently-selected schema.
	// Re-runs whenever the user picks a different level (region → zone →
	// depot → group) so the lower selects cascade down. Surveys don't have a
	// cycle, so we pass undefined and rely on the updated createAreaFilters
	// that skips cycle when empty.
	useEffect(() => {
		if (schemaWorkspace !== '') {
			dispatch(get_areas(schemaWorkspace, undefined, areaFilters));
		}
	}, [schemaWorkspace, areaFilters]);

	// Route search doesn't make sense when the picked schema has no workspace
	// (no areas to filter by). Fall back to no-selection so the user picks
	// again consciously — using the same handler that clears every side.
	useEffect(() => {
		if (searchType === 'route' && !schemaWorkspace) {
			onSearchTypeChange('');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [schemaWorkspace, searchType]);

	const onChangeOrgFilters = (e, name) => {
		let filters = {...orgFilters};
		let keys = Object.keys(filters);

		let idx = keys.indexOf(name);
		for (let index = idx; index < keys.length; index++) {
			const key = keys[index];
			filters[key] = '';
		}

		if (e !== null) {
			filters[name] = e.value;
		}

		// Changing organization or schema swaps the workspace scope (or
		// removes it entirely if the new schema has no workspace), so reset
		// any area drilldown that was picked to avoid shipping stale ids.
		if (name === 'organization' || name === 'schema') {
			setAreaFilters({ region: '', zone: '', depot: '', group: '', route: '' });
		}

		setDisplaySurveys(false);

		localStorage.setItem('organization', filters.organization);

		setOrgFilters(filters);
	}

	const onChangeAreaFilters = (e, name) => {
		const filters = { ...areaFilters };
		const keys = Object.keys(filters);

		if (e === null) {
			// Cleared a level → clear it and everything below to keep the
			// cascade consistent (mirrors AllPayloads' behaviour).
			const idx = keys.indexOf(name);
			for (let i = idx; i < keys.length; i++) {
				filters[keys[i]] = '';
			}
		}
		else {
			filters[name] = e.value;
		}

		setAreaFilters(filters);
	}

	const searchSurveys = (e) => {
		e.preventDefault();

		if (orgFilters.organization === '' || orgFilters.schema === '') {
			setAlert(true);
			window.scroll({ top: 0, behavior: 'smooth' });
			return;
		}
		setAlert(false);

		const geoFilters = buildGeoFilters();
		const listFilters = {
			organization: orgFilters.organization,
			schema: orgFilters.schema,
			...buildDateFilters(),
			...geoFilters,
		};

		if (skip !== 0) {
			setSkip(0);
		}
		else {
			dispatch(list_survey_responses({
				...listFilters,
				include_area: true,
				select: LIST_SELECT,
				skip: 0,
				limit,
			}));
		}

		// Daily chart lives at the search level, not the pagination level: it
		// summarises the whole period, so we fire it here (and NOT inside the
		// [skip, limit] effect) to avoid a wasted request on every page turn.
		dispatch(get_survey_daily_counts({
			organization: orgFilters.organization,
			schema: orgFilters.schema,
			...buildChartDateFilters(),
			...geoFilters,
		}));

		setDisplaySurveys(true);
	}

	const goToResponse = async (e) => {
		e.preventDefault();

		const trimmed = responseId.trim();
		if (trimmed === '') {
			setIdAlert(true);
			return;
		}
		setIdAlert(false);

		try {
			const data = await dispatch(lookup_survey_response(trimmed));
			const id = data?._id?.$oid ?? data?._id;
			if (!id) {
				setIdAlert(true);
				return;
			}
			window.open(`/surveys/responses/${id}`, '_blank', 'noopener,noreferrer');
		}
		catch {
			setIdAlert(true);
		}
	}

	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
			return;
		}

		dispatch(list_survey_responses({
			organization: orgFilters.organization,
			schema: orgFilters.schema,
			...buildDateFilters(),
			...buildGeoFilters(),
			include_area: true,
			select: LIST_SELECT,
			skip,
			limit,
		}));
	}, [skip, limit]);

	// Clear every filter EXCEPT the picked organization so the user can start
	// a fresh search on the same org without re-selecting it. Also collapses
	// the results view so the previous table/chart isn't left behind stale.
	const clearFilters = () => {
		setOrgFilters((prev) => ({ ...prev, schema: '' }));
		setAreaFilters({ region: '', zone: '', depot: '', group: '', route: '' });
		setStoreCode('');
		setSearchType('');
		setDateFrom('');
		setDateTo('');
		setAlert(false);
		setDisplaySurveys(false);
	};

	const rows = survey_list?.responses ?? [];
	const surveysCount = survey_list?.count ?? 0;
	const nPages = Math.ceil(surveysCount / limit);

	const spinnerColor = localStorage.getItem('theme') === 'dark' ? '#1a4e77' : '#004982';

	return (
		<div id={localStorage.getItem('theme')}>
			<div className='container container-padding mt-3' style={{ marginBottom: '7rem' }}>
				<h2 className='text-center'>{t('surveys.list.title')}</h2>

				{/* Quick Lookup — its own form so hitting Enter inside the id
				    input doesn't trigger the search below. Inline layout keeps it
				    compact at the top; drops to a stack on narrow screens. */}
				<form onSubmit={goToResponse} className='mb-4'>
					<p className='fs-5 mb-1'>{t('surveys.list.quickLookup.title')}</p>
					<p className='text-muted small mb-2'>{t('surveys.list.quickLookup.help')}</p>
					{idAlert ? <Alert type='danger' msg={t('surveys.list.quickLookup.invalidId')} /> : null}
					<div className='row g-2 align-items-center'>
						<div className='col-12 col-sm-auto'>
							<label htmlFor='quickLookupInput' className='col-form-label'>
								<i className='bi bi-hash ms-1 me-2'></i>{t('surveys.list.quickLookup.idLabel')}
							</label>
						</div>
						<div className='col-12 col-sm'>
							<input
								id='quickLookupInput'
								type='text'
								className='form-control'
								value={responseId}
								onChange={(e) => setResponseId(e.target.value)}
								placeholder={t('surveys.list.quickLookup.placeholder')}
							/>
						</div>
						{/* d-grid on mobile stretches the button to the row width;
						    d-sm-block restores natural width from `sm` upward. */}
						<div className='col-12 col-sm-auto d-grid d-sm-block'>
							<button className='btn btn-outline-secondary' type='submit' disabled={survey_lookup_loading}>
								{survey_lookup_loading ? t('surveys.list.quickLookup.loading') : t('surveys.list.quickLookup.goBtn')}
							</button>
						</div>
					</div>
				</form>

				<hr className='my-4' />

				<form>
					{alert ? <Alert type='danger' msg={t('allPayloads.errorAlertMsg')} /> : null}

					{organizations.length > 1 &&
						<div className='mb-3'>
							<label className='form-label'>
								<i className='bi bi-house ms-1 me-2'></i> {t('allPayloads.filters.organization')}
							</label>
							<SelectButton
								options={organizations !== undefined ? organizations : ['No organizations']}
								name='organization'
								value={orgFilters.organization === '' ? null : orgFilters.organization}
								onChange={onChangeOrgFilters}
							/>
							<div id='organizationHelp' className='form-text'>{t('allPayloads.filters.orgLegend')}</div>
						</div>
					}

					<div className='mb-3'>
						<label className='form-label'>
							<i className='bi bi-diagram-3 ms-1 me-2'></i> {t('surveys.list.filters.schema')}
						</label>
						<SelectButton
							options={schemaOptions}
							name='schema'
							value={orgFilters.schema === '' ? null : orgFilters.schema}
							onChange={onChangeOrgFilters}
						/>
						<div id='schemaHelp' className='form-text'>{t('surveys.list.filters.schemaLegend')}</div>
					</div>

					{orgFilters.schema && (
					<div className='mb-3'>
						<label className='form-label'>
							<i className='bi bi-search ms-1 me-2'></i> {t('surveys.list.filters.searchType.label')}
						</label>
						<SelectButton
							options={[
								{ value: 'code', name: t('surveys.list.filters.searchType.byCode') },
								...(schemaWorkspace
									? [{ value: 'route', name: t('surveys.list.filters.searchType.byRoute') }]
									: []
								)
							]}
							name='searchType'
							value={searchType === '' ? null : searchType}
							onChange={(e) => onSearchTypeChange(e === null ? '' : e.value)}
						/>
						{!schemaWorkspace && (
							<div className='form-text'>{t('surveys.list.filters.searchType.routeUnavailable')}</div>
						)}
					</div>
					)}

					{orgFilters.schema && searchType === 'code' && (
					<div className='mb-3'>
						<label className='form-label'>
							<i className='bi bi-shop ms-1 me-2'></i> {t('surveys.list.filters.code')}
						</label>
						<input
							type='text'
							className='form-control'
							value={storeCode}
							onChange={(e) => setStoreCode(e.target.value)}
							placeholder={t('surveys.list.filters.codePlaceholder')}
						/>
						<div id='codeHelp' className='form-text'>{t('surveys.list.filters.codeLegend')}</div>
					</div>
					)}

					{orgFilters.schema && searchType === 'route' && schemaWorkspace && (
					<div className='mb-3'>
						<label className='form-label'>
							<i className='bi bi-globe2 ms-1 me-2'></i> {t('allPayloads.filters.areas')}
						</label>
						<div className='row g-3'>
							<div className='col-12 col-md-6 col-lg-4'>
								<label className='form-label small text-muted mb-1'>
									{t('allPayloads.filters.areasOptions.region')}
								</label>
								<SelectButton
									options={'regions' in areas ? areas.regions : []}
									name='region'
									value={areaFilters.region === '' ? null : areaFilters.region}
									onChange={onChangeAreaFilters}
								/>
							</div>
							<div className='col-12 col-md-6 col-lg-4'>
								<label className='form-label small text-muted mb-1'>
									{t('allPayloads.filters.areasOptions.zone')}
								</label>
								<SelectButton
									options={'zones' in areas ? areas.zones : []}
									name='zone'
									value={areaFilters.zone === '' ? null : areaFilters.zone}
									onChange={onChangeAreaFilters}
								/>
							</div>
							<div className='col-12 col-md-6 col-lg-4'>
								<label className='form-label small text-muted mb-1'>
									{t('allPayloads.filters.areasOptions.depot')}
								</label>
								<SelectButton
									options={'depots' in areas ? areas.depots : []}
									name='depot'
									value={areaFilters.depot === '' ? null : areaFilters.depot}
									onChange={onChangeAreaFilters}
								/>
							</div>
							<div className='col-12 col-md-6 col-lg-4'>
								<label className='form-label small text-muted mb-1'>
									{t('allPayloads.filters.areasOptions.group')}
								</label>
								<SelectButton
									options={'groups' in areas ? areas.groups : []}
									name='group'
									value={areaFilters.group === '' ? null : areaFilters.group}
									onChange={onChangeAreaFilters}
								/>
							</div>
							<div className='col-12 col-md-6 col-lg-4'>
								<label className='form-label small text-muted mb-1'>
									{t('allPayloads.filters.areasOptions.route')}
								</label>
								<SelectButton
									options={'routes' in areas ? areas.routes : []}
									name='route'
									value={areaFilters.route === '' ? null : areaFilters.route}
									onChange={onChangeAreaFilters}
								/>
							</div>
						</div>
						<div id='areasHelp' className='form-text mt-2'>{t('surveys.list.filters.areasLegend')}</div>
					</div>
					)}

					<div className='row mb-3'>
						<div className='col-md-6 col-sm-12 mb-3 mb-md-0'>
							<label className='form-label'>
								<i className='bi bi-calendar-event ms-1 me-2'></i> {t('surveys.list.filters.from')}
							</label>
							<input
								type='date'
								className='form-control'
								value={dateFrom}
								max={dateTo || todayIso}
								onChange={(e) => setDateFrom(e.target.value)}
							/>
						</div>
						<div className='col-md-6 col-sm-12'>
							<label className='form-label'>
								<i className='bi bi-calendar-event ms-1 me-2'></i> {t('surveys.list.filters.to')}
							</label>
							<input
								type='date'
								className='form-control'
								value={dateTo}
								min={dateFrom || undefined}
								max={todayIso}
								onChange={(e) => setDateTo(e.target.value)}
							/>
						</div>
						<div className='form-text'>{t('surveys.list.filters.dateLegend')}</div>
					</div>

					{/* Mobile: stacked (Search on top, Clear below) via d-grid;
					    Sm+: side-by-side row via d-sm-flex + justify-content-sm-center. */}
					<div className='d-grid d-sm-flex justify-content-sm-center gap-2 mb-4'>
						<button className='btn submit-btn d-flex align-items-center justify-content-center gap-2' type='submit' onClick={searchSurveys} style={{ minWidth: '180px' }}>
							<Search size={14} />
							<span>{t('allPayloads.searchBtn')}</span>
						</button>
						<button className='btn btn-outline-secondary d-flex align-items-center justify-content-center gap-2' type='button' onClick={clearFilters} style={{ minWidth: '180px' }}>
							<Eraser size={14} />
							<span>{t('surveys.list.filters.clear', { defaultValue: 'Limpiar filtros' })}</span>
						</button>
					</div>
				</form>

				{displaySurveys
					? <div style={{ marginTop: '3rem', minHeight: '510px' }}>
							<SurveyDailyChart
								days={survey_daily?.days}
								total={survey_daily?.total}
								loading={survey_daily_loading}
								locale={localStorage.getItem('i18nextLng')}
							/>
							<Table responsive='lg' className='sl-results-table'>
								<thead className='table-head'>
									<tr>
										<th scope='col'>{t('surveys.list.table.depot')}</th>
										<th scope='col'>{t('surveys.list.table.route')}</th>
										<th scope='col'>{t('surveys.list.table.name')}</th>
										<th scope='col'>{t('surveys.list.table.code')}</th>
										<th scope='col'>{t('surveys.list.table.photos')}</th>
										<th scope='col'>{t('payloadsTable.headers.date')}</th>
										<th scope='col'>{t('payloadsTable.headers.action')}</th>
									</tr>
								</thead>
								<tbody>
									{survey_list_loading
										? <tr>
												<td className='text-center' colSpan={7}>
													<ClipLoader color={spinnerColor} size={40} speedMultiplier={1} cssOverride={{ 'borderWidth': '3px' }} />
												</td>
											</tr>
										: <Fragment>
												{rows.length > 0
													? rows.map((row, idx) => {
															const id = row._id?.$oid ?? row._id;
															const dateEpoch = row.date?.$date;
															const store = row.area?.location;
															const route = row.area?.route;
															const depot = row.area?.depot;
															// Each area leg can come back null independently. For
															// Store we fall back to `fields.code` / `fields.location_name`
															// when `area.location` isn't resolved yet — the doc says
															// they're equivalent, and it keeps the cell useful for
															// rows the enrichment hasn't caught up with.
															const storeCode = store?.code ?? row.fields?.code ?? null;
															const storeName = store?.name ?? row.fields?.location_name ?? null;
															const routeLabel = route?.code ?? '—';
															const depotLabel = depot?.name ?? '—';
															return (
																<tr key={idx}>
																	<td>{depotLabel}</td>
																	<td>{routeLabel}</td>
																	<td>{storeName ?? '—'}</td>
																	<td>{storeCode ?? '—'}</td>
																	<td>{row.n_images ?? 0}</td>
																	<td>{dateEpoch ? epochToLocalDate(dateEpoch, localStorage.getItem('i18nextLng')) : ''}</td>
																	<td>
																		<div className='d-inline-flex gap-2'>
																			<CopyOidButton value={id} label={t('surveys.list.table.copyOid', { defaultValue: 'Copiar ID' })} />
																			<a
																				className='sl-action-btn'
																				href={`/surveys/responses/${id}`}
																				target='_blank'
																				rel='noopener noreferrer'
																				title={t('surveys.list.table.action')}
																				aria-label={t('surveys.list.table.action')}
																			>
																				<ExternalLink size={14} />
																			</a>
																		</div>
																	</td>
																</tr>
															);
														})
													: <tr><td className='text-center' colSpan={7}>{t('surveys.list.table.noResults')}</td></tr>
												}
											</Fragment>
									}
								</tbody>
							</Table>
							<TablePagination
								count={surveysCount}
								arrayLength={rows.length}
								nPages={nPages}
								skip={skip}
								setSkip={setSkip}
								limit={limit}
								setLimit={setLimit}
							/>
						</div>
					: null
				}
			</div>
		</div>
	);
}

export default SurveyList;
